import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LayoutDashboard, Calculator, GitCompare, MapPin, Plus, BarChart3, Shield, LogOut, Download, X } from "lucide-react";
import api from "@/lib/api";
import Dashboard from "@/pages/Dashboard";
import Appraisal from "@/pages/Appraisal";
import Compare from "@/pages/Compare";
import AIPanel from "@/pages/AIPanel";
import Sensitivity from "@/pages/Sensitivity";
import RiskMatrix from "@/pages/RiskMatrix";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import SharedView from "@/pages/SharedView";
import Landing from "@/pages/Landing";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/appraisal", icon: Calculator, label: "New Appraisal" },
  { to: "/compare", icon: GitCompare, label: "Compare" },
  { to: "/sensitivity", icon: BarChart3, label: "Sensitivity" },
  { to: "/risk-matrix", icon: Shield, label: "Risk Matrix" },
  { to: "/ai", icon: MapPin, label: "Location AI" },
];

function AppLayout({ user, onLogout, installPrompt, showInstall, onDismissInstall }) {
  const navigate = useNavigate();

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice;
      onDismissInstall();
    }
  };

  return (
    <div className="app-layout">
      <div className="texture-overlay" />
      {showInstall && (
        <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", zIndex: 1000, background: "hsl(var(--foreground))", color: "white", padding: "12px 20px", borderRadius: 12, display: "flex", alignItems: "center", gap: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.3)", maxWidth: 400 }} data-testid="pwa-install-banner">
          <Download size={18} />
          <span style={{ fontSize: 13, flex: 1 }}>Install Toro Appraisal for quick access</span>
          <button onClick={handleInstall} style={{ background: "white", color: "hsl(var(--foreground))", border: "none", padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }} data-testid="pwa-install-btn">Install</button>
          <button onClick={onDismissInstall} style={{ background: "transparent", border: "none", color: "white", cursor: "pointer", padding: 4 }}><X size={16} /></button>
        </div>
      )}
      <aside className="app-sidebar" data-testid="sidebar">
        <div className="sidebar-logo">
          <img src="/toro-logo.png" alt="Toro Strategic" style={{ height: 44, objectFit: "contain" }} data-testid="sidebar-logo" />
          <span>Development Appraisal</span>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
          <div style={{ flex: 1 }} />
          {user && (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "hsl(var(--muted-foreground))", borderTop: "1px solid hsl(var(--border))", marginBottom: 4 }}>
              <div style={{ fontWeight: 600, color: "hsl(var(--foreground))", marginBottom: 2 }}>{user.name}</div>
              <div style={{ fontSize: 11 }}>{user.email}</div>
            </div>
          )}
          <button
            className="nav-item"
            style={{ background: "hsl(var(--accent))", color: "white", justifyContent: "center", marginTop: 4 }}
            onClick={() => navigate("/appraisal")}
            data-testid="new-appraisal-btn"
          >
            <Plus size={16} />
            New Appraisal
          </button>
          {user && (
            <button
              className="nav-item"
              style={{ justifyContent: "center", marginTop: 4, fontSize: 12, color: "hsl(var(--muted-foreground))" }}
              onClick={onLogout}
              data-testid="logout-btn"
            >
              <LogOut size={14} />
              Sign Out
            </button>
          )}
        </nav>
      </aside>
      <main className="app-main" style={{ position: "relative", zIndex: 1 }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/appraisal" element={<Appraisal />} />
          <Route path="/appraisal/:id" element={<Appraisal />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/sensitivity" element={<Sensitivity />} />
          <Route path="/risk-matrix" element={<RiskMatrix />} />
          <Route path="/ai" element={<AIPanel />} />
        </Routes>
      </main>
      <Toaster theme="light" position="top-right" richColors />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Pick up user data passed via location.state from AuthCallback
  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setChecked(true);
      // Clear state so it doesn't re-trigger on back/forward navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Check session cookie on mount (handles page refresh)
  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setChecked(true);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      // Not authenticated
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    if (!user) checkAuth();
  }, [user, checkAuth]);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    setUser(null);
    navigate("/");
  };

  // Check URL fragment for session_id — process synchronously before routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (!checked) return null;

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    );
  }

  return <AppLayout user={user} onLogout={handleLogout} installPrompt={installPrompt} showInstall={showInstall} onDismissInstall={() => setShowInstall(false)} />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/shared/:token" element={<SharedView />} />
        <Route path="*" element={<AppRouter />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
